import React, { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";

const DEFAULT_RULES = {
  max_daily_calories: "",
  min_daily_calories: "",
  max_weekly_weight_loss_lbs: "",
  tone: "encouraging",
  notes_for_coach: "",
};

export default function CoachRulesModal({
  isOpen,
  onClose,
  token,
  userId,
  titleName = "User",
  readOnly = false,
  onSaved,
  showToast,
}) {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState(DEFAULT_RULES);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const jsonHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  useEffect(() => {
    if (!isOpen || !token || !userId) return;

    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/coach-rules/${userId}`, {
          headers: authHeaders,
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to load coach rules");
        }

        const data = await res.json();
        const r = data?.rules || {};

        setRules({
          max_daily_calories: r.max_daily_calories ?? "",
          min_daily_calories: r.min_daily_calories ?? "",
          max_weekly_weight_loss_lbs: r.max_weekly_weight_loss_lbs ?? "",
          tone: r.tone || "encouraging",
          notes_for_coach: r.notes_for_coach || "",
        });
      } catch (e) {
        console.warn("[CoachRulesModal] load:", e.message);
        setRules(DEFAULT_RULES);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, token, userId, authHeaders]);

  const save = async () => {
    if (!token || !userId) return;

    const payload = {
      user_id: userId,
      tone: rules.tone,
      notes_for_coach: rules.notes_for_coach || null,
      max_daily_calories:
        rules.max_daily_calories === ""
          ? null
          : Number(rules.max_daily_calories),
      min_daily_calories:
        rules.min_daily_calories === ""
          ? null
          : Number(rules.min_daily_calories),
      max_weekly_weight_loss_lbs:
        rules.max_weekly_weight_loss_lbs === ""
          ? null
          : Number(rules.max_weekly_weight_loss_lbs),
    };

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/coach-rules/${userId}`, {
        method: "PUT",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to save coach rules");
      }

      setRules((prev) => ({ ...prev }));

      showToast?.("Coach rules saved!", "success");
      try {
        onSaved?.();
      } catch {}
      onClose?.();
    } catch (e) {
      showToast?.("Error saving coach rules: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-content"
        style={{
          background: "var(--bg-modal)",
          color: "var(--text-primary)",
          borderRadius: 16,
          width: "100%",
          maxWidth: 820,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          border: "1px solid var(--border-light)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid var(--border-light)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>
              🤖 Coach Rules — {titleName}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Only calorie limits, weight-loss pace, tone, and coach notes stay here. Food, injury, and equipment preferences now live in the questionnaire.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: "1px solid var(--border-light)",
              background: "var(--bg-card)",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: "34px",
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: 18,
            overflowY: "auto",
            maxHeight: "calc(100vh - 160px)",
          }}
        >
          {loading ? (
            <p className="body-medium">Loading…</p>
          ) : (
            <>
              <div
                className="form-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div className="form-group">
                  <label className="body-medium">Min daily calories</label>
                  <input
                    className="form-input"
                    type="number"
                    value={rules.min_daily_calories}
                    onChange={(e) =>
                      setRules((prev) => ({
                        ...prev,
                        min_daily_calories: e.target.value,
                      }))
                    }
                    placeholder="e.g., 1600"
                    disabled={readOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Max daily calories</label>
                  <input
                    className="form-input"
                    type="number"
                    value={rules.max_daily_calories}
                    onChange={(e) =>
                      setRules((prev) => ({
                        ...prev,
                        max_daily_calories: e.target.value,
                      }))
                    }
                    placeholder="e.g., 2400"
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div
                className="form-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div className="form-group">
                  <label className="body-medium">
                    Max weekly weight loss (lbs)
                  </label>
                  <input
                    className="form-input"
                    type="number"
                    step="0.1"
                    value={rules.max_weekly_weight_loss_lbs}
                    onChange={(e) =>
                      setRules((prev) => ({
                        ...prev,
                        max_weekly_weight_loss_lbs: e.target.value,
                      }))
                    }
                    placeholder="e.g., 2.0"
                    disabled={readOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Tone</label>
                  <select
                    className="form-input"
                    value={rules.tone}
                    onChange={(e) =>
                      setRules((prev) => ({
                        ...prev,
                        tone: e.target.value,
                      }))
                    }
                    disabled={readOnly}
                  >
                    <option value="encouraging">Encouraging</option>
                    <option value="direct">Direct</option>
                    <option value="strict">Strict</option>
                    <option value="gentle">Gentle</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="body-medium">Notes for coach</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={rules.notes_for_coach}
                  onChange={(e) =>
                    setRules((prev) => ({
                      ...prev,
                      notes_for_coach: e.target.value,
                    }))
                  }
                  placeholder="Any extra constraints or coaching instructions..."
                  disabled={readOnly}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Close
                </button>

                {!readOnly && (
                  <button
                    className="btn-primary"
                    onClick={save}
                    disabled={loading}
                  >
                    Save Rules
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}